<template>
  <div class="settings role-settings">
    <template v-if="teamRole && loaded">
      <VPBreadcrumbs :elements="[{ text: 'Roles', to: parentRoute }, { text: teamRole.name }]" />
      <Form
        :form-id="formId"
        :initial-values="initialValues"
      >
        <HiddenInput input-id="id" />
        <VPInput input-id="name" />
        <FieldsFor name="permissionAttributes">
          <div class="flex sm:flex-row flex-wrap gap-8">
            <div
              v-for="(permissionGroup, key) in teamRole.permissionGroups"
              :key="key"
              class="sm:w-max w-96"
            >
              <div class="bg-vp-base-900 px-4 py-1.5 rounded-t-lg overflow-hidden relative">
                <span class="text-sm font-semibold text-white font-semibold">{{ permissionGroup.label }}</span>
              </div>
              <div class="bg-white sm:flex sm:flex-row gap-4 pl-4 pr-12 py-4 border rounded-b-lg">
                <div
                  v-for="permission in permissionGroup.permissions"
                  :key="permission.key"
                  class="space-y-0.5"
                >
                  <span class="text-xs font-bold">{{ permission.label }}</span>
                  <VPDropdown
                    :input-id="permission.key"
                    :options="(permission?.options || []).map(({ label, value }) => ({ name: label, value }))"
                  />
                </div>
              </div>
            </div>
            <div class="sm:w-max w-96">
              <div class="bg-vp-base-900 px-4 py-1.5 rounded-t-lg overflow-hidden relative">
                <span class="text-sm font-semibold text-white font-semibold mb-2">Allowances</span>
              </div>
              <div class="bg-white sm:grid grid-cols-3 gap-y-2 gap-x-4 pl-4 pr-12 py-4 border rounded-b-lg">
                <VPCheckbox
                  v-for="(allowance, index) in teamRole?.allowances"
                  :key="index"
                  :label="allowance.label"
                  :input-id="allowance.key"
                  :true-value="true"
                  :false-value="false"
                />
              </div>
            </div>
          </div>
          <FormActionButtons
            :show-save="isFormDirty()"
            :show-cancel="isFormDirty()"
            class="form-buttons"
            show-delete
            @cancel="onCancel"
            @save="handleSave"
            @delete="triggerDeleteConfirmation"
          />
        </FieldsFor>
      </Form>
    </template>
    <VPModal
      v-model:show="showDeleteConfirmation"
      title="Confirm Role Deletion"
      confirm-label="Delete"
      close-label="Cancel"
      size="sm"
      @confirm="confirmDelete"
      @close="closeModal"
    >
      <p class="text-center text-vp-base-900">
        Are you sure you want to delete role '{{ teamRole.name }}'?
      </p>
    </VPModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { Form, HiddenInput, useFormsStoreComposable } from 'vuniform';
import VPBreadcrumbs from '@/components/VPBreadcrumbs.vue';
import VPDropdown from '@/components/form/VPDropdown.vue';
import VPCheckbox from '@/components/form/VPCheckbox.vue';
import VPInput from '@/components/form/VPInput.vue';
import { FieldsFor } from 'vuniform';
import { useTeamRolesStore } from '@/stores/team-roles';
import FormActionButtons from '@/components/form/FormActionButtons.vue';
import type { TeamRoleInput } from '@/generated/graphql';
import { useRootStore } from '@/stores/root';
import VPModal from '@/components/form/VPModal.vue';

const rootStore = useRootStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const teamRolesStore = useTeamRolesStore();
const { formId, formsStore, formGetValues, isFormDirty } = useFormsStoreComposable('edit-roles-form');
const parentRoute = { name: 'RoleIndex' };

const teamRole = computed(() => {
  return teamRolesStore.teamRoleDetails;
});

interface InitialValues {
  [key: string]: any;
}
const loaded = ref(false);
const initialValues: InitialValues = reactive({ permissionAttributes: {} });

onMounted(async () => {
  await teamRolesStore.fetchTeamRole(Number.parseInt(route.params.roleId));
  initialValues.name = teamRole.value?.name;
  initialValues.id = teamRole.value?.id;
  for (const pg of teamRole?.value?.permissionGroups || []) {
    for (const p of pg.permissions) {
      initialValues.permissionAttributes[p.key] = p.level;
    }
  }
  for (const a of teamRole?.value?.allowances || []) {
    initialValues.permissionAttributes[a.key] = a.enabled;
  }
  loaded.value = true;
});

const handleSave = async () => {
  const values = formGetValues<TeamRoleInput>();
  await teamRolesStore.saveTeamRole({
    ...values,
    accountId: rootStore.currentAccountId,
  });
  toast.success(`Role '${teamRole.value?.name}' has been saved succesfully!`);
  router.push(parentRoute);
};

const onCancel = () => formsStore.RESET_FIELD_VALUES(formId);
const showDeleteConfirmation = ref(false);

const triggerDeleteConfirmation = () => {
  showDeleteConfirmation.value = true;
};

const confirmDelete = async () => {
  await teamRolesStore.deleteTeamRole(teamRole.value?.id);
  showDeleteConfirmation.value = false;
  toast.success(`Role '${teamRole.value?.name}' has been deleted!`);
  router.push(parentRoute);
};

const closeModal = () => {
  showDeleteConfirmation.value = false;
};
</script>
